// Copyright (c) Ping. All rights reserved.
//
// GIF search provider contract — the chat-room composer's GIF panel
// (Figma `2804:27348` "GIF container") is a presentational component
// that consumes a host-supplied adapter for trending + query results.
// The web host's adapter wraps the Tenor v2 REST API; future hosts
// (desktop, mobile) can swap in their own provider without touching
// the UI layer.
//
// Both methods return readonly arrays of `GifItem`. Implementations
// MUST resolve to an empty array when:
//   - the underlying API key is missing (the web host's
//     `createTenorProvider` returns an "empty" provider so the panel
//     still renders gracefully — empty state, no errors).
//   - the network request fails (catch + log + return []).
// The composer treats `[]` as the empty / loading-complete state.

/**
 * A single GIF result. `previewUrl` is a small static or low-fps
 * preview shown in the grid; `fullUrl` is the file actually sent
 * over the wire (encoded into the `com.ping.gif` envelope). `width`
 * / `height` are the intrinsic dimensions in CSS pixels and let the
 * receiver render the placeholder bubble before the bytes load.
 */
export interface GifItem {
  readonly id: string;
  readonly previewUrl: string;
  readonly fullUrl: string;
  readonly width: number;
  readonly height: number;
  readonly altText?: string | undefined;
}

/**
 * Host-supplied search adapter. The composer calls `trending()` on
 * first open + when the query is empty; it calls `search(query)` on
 * non-empty input.
 *
 * `limit` is advisory — the implementation is free to cap below the
 * requested count.
 */
export interface GifSearchProvider {
  search(query: string, opts?: { limit?: number }): Promise<readonly GifItem[]>;
  trending(opts?: { limit?: number }): Promise<readonly GifItem[]>;
}

/**
 * Empty provider used by hosts that want the GIF panel to render but
 * have no API key configured. The panel still draws (empty state,
 * search input, tabs) so the user understands the feature exists.
 */
export const emptyGifProvider: GifSearchProvider = {
  async search() {
    return [];
  },
  async trending() {
    return [];
  },
};

/**
 * Sticker search adapter. Same shape as `GifSearchProvider` — the
 * unified Emoji / GIFs / Stickers popover reuses the GIF panel
 * layout for the Stickers tab. Hosts typically wrap the same
 * upstream API as GIFs (Tenor exposes `searchfilter=sticker` for a
 * sticker-only result set).
 */
export interface StickerSearchProvider {
  search(query: string, opts?: { limit?: number }): Promise<readonly GifItem[]>;
  trending(opts?: { limit?: number }): Promise<readonly GifItem[]>;
}

/**
 * Fallback provider when no sticker source is configured.
 */
export const emptyStickerProvider: StickerSearchProvider = {
  async search() {
    return [];
  },
  async trending() {
    return [];
  },
};
